import React from "react";
import { Check } from "lucide-react";

/** Multi-select de chips para tipos de línea.
 *  Soporta: diseno, produccion, envio, montaje, desmontaje, retirada, otros.
 *  Visual: chips clickables con color/icon distintivos.
 *  Props: { value: string[], onChange: (string[]) => void, compact?: bool }
 */
export const TIPOS_LINEA = [
  { key: "diseno", label: "Diseño", icon: "🎨", color: "fuchsia" },
  { key: "produccion", label: "Producción", icon: "🏭", color: "violet" },
  { key: "envio", label: "Envío", icon: "🚚", color: "sky" },
  { key: "montaje", label: "Montaje", icon: "🔧", color: "emerald" },
  { key: "desmontaje", label: "Desmontaje", icon: "↩️", color: "orange", legacy: true },  // Fernando 31/05/26: migrar a retirada
  { key: "retirada", label: "Retirada", icon: "🗑️", color: "amber" },
  { key: "otros", label: "Otros", icon: "📋", color: "zinc" },
];

const COLOR_CLASSES = {
  fuchsia: { on: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/50", off: "bg-zinc-900/40 text-zinc-500 border-zinc-800 hover:border-fuchsia-500/30 hover:text-fuchsia-300" },
  violet: { on: "bg-violet-500/20 text-violet-200 border-violet-500/50", off: "bg-zinc-900/40 text-zinc-500 border-zinc-800 hover:border-violet-500/30 hover:text-violet-300" },
  sky: { on: "bg-sky-500/20 text-sky-200 border-sky-500/50", off: "bg-zinc-900/40 text-zinc-500 border-zinc-800 hover:border-sky-500/30 hover:text-sky-300" },
  emerald: { on: "bg-emerald-500/20 text-emerald-200 border-emerald-500/50", off: "bg-zinc-900/40 text-zinc-500 border-zinc-800 hover:border-emerald-500/30 hover:text-emerald-300" },
  orange: { on: "bg-orange-500/20 text-orange-200 border-orange-500/50", off: "bg-zinc-900/40 text-zinc-500 border-zinc-800 hover:border-orange-500/30 hover:text-orange-300" },
  amber: { on: "bg-amber-500/20 text-amber-200 border-amber-500/50", off: "bg-zinc-900/40 text-zinc-500 border-zinc-800 hover:border-amber-500/30 hover:text-amber-300" },
  zinc: { on: "bg-zinc-700/40 text-zinc-200 border-zinc-600", off: "bg-zinc-900/40 text-zinc-500 border-zinc-800 hover:border-zinc-600 hover:text-zinc-300" },
};

export default function TiposLineaMulti({ value = [], onChange, compact = false, dataTestId = "tipos-linea-multi" }) {
  const toggle = (key) => {
    const set = new Set(value);
    if (set.has(key)) set.delete(key); else set.add(key);
    onChange(Array.from(set));
  };

  const px = compact ? "px-2 py-0.5" : "px-2.5 py-1";
  const text = compact ? "text-[10px]" : "text-xs";
  const gap = compact ? "gap-1" : "gap-1.5";

  return (
    <div className={`flex flex-wrap ${gap}`} data-testid={dataTestId}>
      {TIPOS_LINEA.map((t) => {
        const on = value.includes(t.key);
        const cls = COLOR_CLASSES[t.color] || COLOR_CLASSES.zinc;
        const legacyCls = t.legacy && !on ? "opacity-50" : "";
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => toggle(t.key)}
            data-testid={`tipo-chip-${t.key}`}
            title={t.legacy ? "Tipo legacy — usa 'Retirada' en su lugar" : undefined}
            className={`inline-flex items-center ${gap} ${px} ${text} rounded-full border font-medium transition-all ${on ? cls.on : cls.off} ${legacyCls}`}
          >
            {on && <Check className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />}
            <span>{t.icon}</span>
            <span>{t.label}</span>
            {t.legacy && (
              <span className="ml-1 px-1 py-px rounded text-[8px] uppercase tracking-wide bg-zinc-700/60 text-zinc-300 font-mono">
                legacy
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Versión read-only: chips estáticos para mostrar los tipos seleccionados. */
export function TiposLineaBadges({ tipos = [], compact = true }) {
  if (!tipos || tipos.length === 0) return null;
  return (
    <div className={`flex flex-wrap ${compact ? "gap-1" : "gap-1.5"}`}>
      {tipos.map((k) => {
        const t = TIPOS_LINEA.find((x) => x.key === k);
        if (!t) return null;
        const cls = COLOR_CLASSES[t.color] || COLOR_CLASSES.zinc;
        return (
          <span key={k}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${cls.on}`}>
            <span>{t.icon}</span><span>{t.label}</span>
          </span>
        );
      })}
    </div>
  );
}
